/** "I am done for today — stop sending me work."
 *
 *  A different thing from the firm's day close, which is an Owner freezing everybody's
 *  counters and running carry-forward. This freezes nothing and changes no task. It
 *  says one thing: do not put anything new on this person's list for this day.
 *
 *  The sentence that matters most in the whole feature is on the banner: **you can
 *  still finish what is already here.** Without it people assume signing off locks
 *  them out, and then do not use it.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type DayClose } from '@/api/client'
import { ErrorNote, fmtStamp } from '@/components/ui'

const MIN_REMARK = 10

/** The banner for somebody who has signed off, with the way back. */
export function SignedOffBanner({ row, day }: { row: DayClose; day: string }) {
  const qc = useQueryClient()
  const [err, setErr] = useState<unknown>(null)
  const back = useMutation({
    mutationFn: () => api.post(`/days/${day}/sign-on`, { user_id: row.user_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['day'] })
      qc.invalidateQueries({ queryKey: ['people'] })
    },
    onError: setErr,
  })
  const at = row.closed_at ? fmtStamp(row.closed_at) : ''
  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="border-l-4 rounded-r-md px-3.5 py-2.5 text-[13px] mb-4
                      bg-primary-tint border-primary text-primary">
        <b>You signed off{at ? ` at ${at}` : ''}.</b>{' '}
        Nothing new will be put on your list for this day.{' '}
        {/* The load-bearing half. */}
        You can still finish what is already here.
        {row.remarks && (
          <span className="block text-[12.3px] mt-1 opacity-80">
            You said: {row.remarks}
          </span>
        )}
        <button className="btn btn-sm mt-2" disabled={back.isPending}
                onClick={() => { setErr(null); back.mutate() }}>
          {back.isPending ? 'Reopening…' : 'Reopen my day'}
        </button>
      </div>
    </>
  )
}

/** The modal. The remark is mandatory, so the button stays disabled until there is
 *  one — the same rule the service enforces, said here so nobody meets it as a 422.
 */
export function SignOffModal({ day, userId, name, outstanding, willTell, onClose }: {
  day: string
  /** Blank means "mine", which is what the route defaults to. */
  userId?: string
  /** Whose day, when it is not your own. */
  name?: string
  outstanding: { open: number; awaiting: number }
  /** Whether there is anything outstanding, which is one of the two conditions for
   *  the people above being told. The other is the firm's end-of-day time, which the
   *  server holds and the browser has no business fetching for a heads-up - so the
   *  wording below is conditional rather than a promise it cannot keep. Said BEFORE
   *  the send either way: learning afterwards that your manager was messaged is a
   *  nasty surprise. */
  willTell: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [remarks, setRemarks] = useState('')
  const [err, setErr] = useState<unknown>(null)
  const save = useMutation({
    mutationFn: () => api.post(`/days/${day}/sign-off`,
                               { remarks, ...(userId ? { user_id: userId } : {}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['day'] })
      qc.invalidateQueries({ queryKey: ['people'] })
      onClose()
    },
    onError: setErr,
  })

  const left = outstanding.open + outstanding.awaiting
  const short = remarks.trim().length < MIN_REMARK
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label="Sign off the day">
      <div className="card w-full max-w-md p-5">
        <b className="text-[14px]">
          {name ? `Sign off ${name}'s day` : 'Sign off for the day'}
        </b>
        {err ? <div className="mt-3"><ErrorNote error={err} /></div> : null}

        <p className="text-[12.8px] text-ink-2 mt-2">
          {left === 0
            ? 'Everything on this day is done.'
            : `${outstanding.open} task${outstanding.open === 1 ? '' : 's'} still open`
              + (outstanding.awaiting
                 ? ` and ${outstanding.awaiting} waiting for a decision` : '')
              + '.'}
        </p>
        <p className="text-[12.5px] text-ink-3 mt-1">
          Nothing new will be added to {name ? 'their' : 'your'} list for it.{' '}
          {name ? 'They' : 'You'} can still finish what is already there.
        </p>

        <label className="label block mt-3.5">Why<span className="text-rej"> *</span></label>
        <textarea rows={3} autoFocus value={remarks} maxLength={1000}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Off to the registrar for the afternoon"
                  className="field w-full resize-none" />
        <div className="flex items-baseline gap-2 mt-1">
          <span className="mono text-[11px] text-ink-3">
            {remarks.trim().length}/{MIN_REMARK}
          </span>
          <span className="text-[11.5px] text-ink-3">
            It is kept with the record{willTell ? ' and sent to the people above' : ''}.
          </span>
        </div>

        {willTell && (
          <p className="text-[12.3px] text-pend-ink bg-pend-tint rounded px-2.5 py-1.5 mt-2.5">
            There is work outstanding, so if this is before the firm's end of day the
            people above {name ? 'them' : 'you'} will be told.
          </p>
        )}

        <div className="flex gap-2 mt-4">
          <button className="btn btn-primary flex-1 justify-center"
                  disabled={short || save.isPending}
                  onClick={() => { setErr(null); save.mutate() }}>
            {save.isPending ? 'Signing off…' : 'Sign off'}
          </button>
          <button className="btn flex-1 justify-center" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

/** The chip a manager sees beside a person's name. */
export function SignOffTag({ row }: { row: DayClose }) {
  if (row.status !== 'CLOSED') return null
  const at = row.closed_at ? fmtStamp(row.closed_at) : ''
  return (
    <span className="text-[10.5px] px-1.5 py-px rounded bg-pend-tint text-pend-ink
                     whitespace-nowrap"
          title={row.remarks || 'Signed off for the day'}>
      signed off{at ? ` ${at}` : ''}
    </span>
  )
}
